import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SaveAlt as SaveAltIcon, RestartAlt as RestartAltIcon } from '@mui/icons-material';
import type { VisibilityState } from '@tanstack/react-table';
import type { Command, GridMetadata } from '../../../menu/types';
import type { ColumnMetadata } from '../../types';
import { DateRangeFilter } from '../filters';
import { SearchBar } from './SearchBar';
import { ColumnChooserPopover } from './ColumnChooserPopover';
import { getCommandId, isDetailNavigationGrid, isRowDeleteCommand } from '../../commands/utils';
import { commandRegistry } from '../../commands';
import { normalizeCommandIcon } from '../../utils/commandIconUtils';
import { HelpOutline as FallbackIcon } from '@mui/icons-material';
import { FilterPanelCommandButton } from '../../commands/filterPanel';
import { FILTER_PANEL_COMMAND_ID } from '../../commands/filterPanelState';
import { getCommandIcon as getRegisteredCommandIcon } from '../../utils/commandIcons';
import { DetailFormActionOverflow } from '../../../detail-form/DetailFormActionOverflow';

const ICON_BUTTON_SX = { minWidth: 38.5, width: 38.5, height: 38.5, p: 1 } as const;
const FA_ICON_SX = { fontSize: '1.25rem', lineHeight: 1, display: 'block' } as const;
const SHOW_DELETED_COMMAND_ID = 'showdeleted';
const GRID_STATE_COMMAND_ID = 'zsitecommandsavegridstate';

/**
 * Command IDs that are rendered in the GridPage header (Save, Revert, Import)
 * and should therefore be hidden from the toolbar when headerActionsExtracted=true.
 * DetailFormGridSection sets headerActionsExtracted=false, keeping them in the toolbar.
 */
const HEADER_EXTRACTED_COMMAND_IDS = new Set(['save', 'revert', 'import']);

type GridActionToolbarProps = {
  metadata: GridMetadata;
  commands?: Command[];
  hasUnsavedChanges: boolean;
  isBatchSaving: boolean;
  onCommandClick: (command: Command) => void;
  showMetadataChips?: boolean;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  showSearchPanel?: boolean;
  columns?: ColumnMetadata[];
  showDeleted?: boolean;
  onShowDeletedToggle?: () => void;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  onSaveGridState?: () => void;
  onClearGridState?: () => void;
  hasSelectedRows?: boolean;
  markedForDeletion?: Set<string>;
  hasPendingChanges?: boolean;
  inlineEditable?: boolean;
  onRollbackSelectedRows?: () => void;
  onColumnChooserClose?: () => void;
  canResetFilters?: boolean;
  filterPanelVisible?: boolean;
  filterPanelAvailable?: boolean;
  onFilterPanelToggle?: () => void;
  headerActionsExtracted?: boolean;
  initialDateRange?: { startDate?: string; endDate?: string };
  commandCaptionOverrides?: Record<string, string>;
};

const buttonColors: Record<
  string,
  'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
> = {
  save: 'primary',
  add: 'primary',
  delete: 'error',
  deleteentities: 'error',
  revert: 'warning',
};

const getButtonColor = (command: Command) => {
  const id =
    command.alias === 'custom' && command.action ? command.action.toLowerCase() : command.alias;
  return buttonColors[id];
};

// Fallback to Material-UI icons based on effective command id
export const GridActionToolbar = ({
  metadata,
  commands,
  hasUnsavedChanges,
  isBatchSaving,
  onCommandClick,
  onDateRangeChange,
  searchTerm,
  onSearchChange,
  showSearchPanel = true,
  columns,
  showDeleted,
  onShowDeletedToggle,
  columnVisibility,
  onColumnVisibilityChange,
  onSaveGridState,
  onClearGridState,
  hasSelectedRows,
  onColumnChooserClose,
  inlineEditable,
  canResetFilters = false,
  filterPanelVisible = true,
  filterPanelAvailable = true,
  onFilterPanelToggle,
  headerActionsExtracted = false,
  initialDateRange,
  commandCaptionOverrides,
}: GridActionToolbarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [columnChooserAnchor, setColumnChooserAnchor] = useState<HTMLElement | null>(null);
  const [gridStateMenuAnchor, setGridStateMenuAnchor] = useState<HTMLElement | null>(null);

  /**
   * Get the appropriate icon for a command
   * Supports both custom icons from backend and fallback Material-UI icons
   */
  const renderFaIcon = (className: string) => (
    <i className={className} style={FA_ICON_SX} aria-hidden />
  );

  const getCommandIcon = (command: Command): React.ReactElement => {
    const commandId = getCommandId(command);

    // Show loading spinner for save command during batch save
    if (commandId === 'save' && isBatchSaving) {
      return <CircularProgress size={20} />;
    }

    // Use custom icon from Umbraco when configured
    const umbracoIcon = normalizeCommandIcon(command.icon);
    if (umbracoIcon) return renderFaIcon(umbracoIcon);
    return getRegisteredCommandIcon(command) ?? <FallbackIcon fontSize="small" />;
  };

  /**
   * Determine if a command button should be disabled based on its properties and current state
   */
  const isCommandDisabled = (command: Command): boolean => {
    const commandId = getCommandId(command);
    const commandConfig = commandRegistry.getConfig(commandId);

    // Registered row-dependent commands require selection regardless of whether
    // legacy metadata represents them as group or action commands.
    if (commandConfig?.requiresSelection && !hasSelectedRows) {
      return true;
    }

    // Group commands require selection by default unless explicitly opted out
    // via requiresSelection=false in the registry config.
    if (command.isGroup && commandConfig?.requiresSelection !== false && !hasSelectedRows) {
      return true;
    }

    // Honor backend-provided flag to require a selection for this command.
    if (command.requireSelection) {
      return !hasSelectedRows;
    }

    // Row-dependent actions should not be clickable when nothing is selected.
    if (['delete', 'deleteentities', 'copyrow'].includes(commandId) && !hasSelectedRows) {
      return true;
    }

    // Save/Revert only apply to inline-editable grids.
    if (['save', 'revert'].includes(commandId) && !inlineEditable) {
      return true;
    }

    // Disable save/revert when no changes exist
    if (['save', 'revert'].includes(commandId)) {
      if (!hasUnsavedChanges) {
        return true;
      }
    }

    // Disable save/revert during batch save operation
    if (['save', 'revert'].includes(commandId) && isBatchSaving) {
      return true;
    }

    if (command.disableDeleted && showDeleted) {
      return true;
    }

    return false;
  };

  const renderColumnChooser = (cmd: Command) => {
    if (!columns || !columnVisibility || !onColumnVisibilityChange) return null;
    return (
      <>
        <Tooltip title={cmd.caption || 'Column Chooser'} arrow>
          <span>
            <Button
              variant="outlined"
              onClick={(e) => setColumnChooserAnchor(e.currentTarget)}
              size="medium"
              sx={ICON_BUTTON_SX}
            >
              {getCommandIcon(cmd)}
            </Button>
          </span>
        </Tooltip>
        <ColumnChooserPopover
          anchorEl={columnChooserAnchor}
          open={Boolean(columnChooserAnchor)}
          onClose={() => {
            setColumnChooserAnchor(null);
            onColumnChooserClose?.();
          }}
          columns={columns}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
        />
      </>
    );
  };

  const renderGridStateMenu = () => {
    if (!onSaveGridState && !onClearGridState) return null;
    return (
      <Popper
        open={Boolean(gridStateMenuAnchor)}
        anchorEl={gridStateMenuAnchor}
        placement="bottom-end"
        transition
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={4}>
              <ClickAwayListener onClickAway={() => setGridStateMenuAnchor(null)}>
                <MenuList>
                  {onSaveGridState && (
                    <MenuItem
                      onClick={() => {
                        onSaveGridState();
                        setGridStateMenuAnchor(null);
                      }}
                    >
                      <SaveAltIcon sx={{ mr: 1 }} fontSize="small" />
                      Save state
                    </MenuItem>
                  )}
                  {onClearGridState && (
                    <MenuItem
                      onClick={() => {
                        onClearGridState();
                        setGridStateMenuAnchor(null);
                      }}
                    >
                      <RestartAltIcon sx={{ mr: 1 }} fontSize="small" />
                      Clear state
                    </MenuItem>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    );
  };

  const renderLeftSideButtons = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
      {/* Grid title rendered for zgridtitle command (location: "before") */}
      {gridTitleCommand && (
        <Typography
          variant="subtitle1"
          component="span"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            mr: 1,
          }}
        >
          {gridTitleCommand.caption}
        </Typography>
      )}

      {resetFiltersCommand && (
        <Tooltip
          title={
            canResetFilters
              ? resetFiltersCommand.caption
              : resetFiltersCommand.hintForStateDisabled || resetFiltersCommand.caption
          }
          arrow
        >
          <span>
            <Button
              variant="outlined"
              color="error"
              onClick={() => onCommandClick(resetFiltersCommand)}
              disabled={!canResetFilters}
              size="medium"
              sx={ICON_BUTTON_SX}
            >
              {getCommandIcon(resetFiltersCommand)}
            </Button>
          </span>
        </Tooltip>
      )}

      {filterPanelCommand && filterPanelAvailable && onFilterPanelToggle && (
        <FilterPanelCommandButton
          command={filterPanelCommand}
          visible={filterPanelVisible}
          onToggle={onFilterPanelToggle}
        />
      )}

      {refreshCommand && (
        <Tooltip title={refreshCommand.caption} arrow>
          <span>
            <Button
              variant="outlined"
              onClick={() => onCommandClick(refreshCommand)}
              size="medium"
              sx={ICON_BUTTON_SX}
            >
              {getCommandIcon(refreshCommand)}
            </Button>
          </span>
        </Tooltip>
      )}

      {!isMobile &&
        dateFilterCommand &&
        onDateRangeChange &&
        dateFilterCommand.location !== 'after' && (
          <Box sx={{ flexBasis: { xs: '100%', md: 'auto' }, width: { xs: '100%', md: 'auto' } }}>
            <DateRangeFilter
              command={dateFilterCommand}
              onDateRangeChange={onDateRangeChange}
              initialDateRange={initialDateRange}
            />
          </Box>
        )}
    </Box>
  );

  const renderRightSideExtras = () => (
    <>
      {!isMobile &&
        dateFilterCommand &&
        onDateRangeChange &&
        dateFilterCommand.location === 'after' && (
          <Box
            sx={{
              flexBasis: { xs: '100%', md: 'auto' },
              width: { xs: '100%', md: 'auto' },
              order: { xs: 2, md: 0 },
            }}
          >
            <DateRangeFilter
              command={dateFilterCommand}
              onDateRangeChange={onDateRangeChange}
              initialDateRange={initialDateRange}
            />
          </Box>
        )}
      {columnChooserCommand && renderColumnChooser(columnChooserCommand)}
      {showSearchPanel && onSearchChange && (
        <SearchBar value={searchTerm || ''} onChange={onSearchChange} />
      )}
    </>
  );

  // --- Command filtering ---

  // GridWithDetail navigates to a separate form — batch save has no meaning there.
  const isDetailNav = isDetailNavigationGrid(metadata, commands);
  const isInlineEditableGrid = Boolean(inlineEditable);

  const visibleCommands = commands?.filter((cmd) => {
    const id = getCommandId(cmd);

    if (isRowDeleteCommand(cmd)) {
      return false;
    }

    /**
     * Legacy (DevExpress) toolbar behavior:
     * - renders group commands (cmd.isGroup)
     * - renders action commands (cmd.action)
     * - also renders "built-in" command aliases supported by SPA (registry) even without action
     *
     * Everything else should be ignored to avoid phantom/duplicate buttons.
     */
    const isBuiltIn =
      commandRegistry.has(id) ||
      id === FILTER_PANEL_COMMAND_ID ||
      id === SHOW_DELETED_COMMAND_ID ||
      id === GRID_STATE_COMMAND_ID;
    if (!cmd.isGroup && !cmd.action && !isBuiltIn) {
      return false;
    }
    if (id === SHOW_DELETED_COMMAND_ID && !onShowDeletedToggle) {
      return false;
    }
    if (id === GRID_STATE_COMMAND_ID && !onSaveGridState && !onClearGridState) {
      return false;
    }

    // When the parent (GridPage) hosts Save / Revert / Import in the header,
    // exclude those commands from the toolbar to avoid duplication.
    if (headerActionsExtracted && HEADER_EXTRACTED_COMMAND_IDS.has(id)) {
      return false;
    }

    return (
      id !== 'resetfilters' && // Shown separately on the left
      id !== 'zgridfilter' && // Date range filter is handled separately
      id !== 'zgridtitle' && // Grid title is handled separately
      id !== 'refresh' && // Shown separately on the left
      id !== FILTER_PANEL_COMMAND_ID && // Filter row visibility toggle rendered separately
      id !== 'columnchooser' && // Column chooser popover rendered separately
      id !== 'deletescreenshot' && // Only used in a detail form screenshot section
      !((isInlineEditableGrid || isDetailNav) && id === 'edit') && // Inline-edit grids omit edit toolbar button
      // When NOT extracted: keep legacy logic (save/revert only for inline grids)
      (!headerActionsExtracted ? isInlineEditableGrid || (id !== 'save' && id !== 'revert') : true)
    );
  });

  const orderedCommands = visibleCommands ?? [];
  const overflowCommands = [
    ...orderedCommands.filter((command) => {
      const commandId = getCommandId(command);
      return commandId !== SHOW_DELETED_COMMAND_ID && commandId !== GRID_STATE_COMMAND_ID;
    }),
    ...orderedCommands.filter((command) => {
      const commandId = getCommandId(command);
      return commandId === SHOW_DELETED_COMMAND_ID || commandId === GRID_STATE_COMMAND_ID;
    }),
  ];
  const commandActionItems = overflowCommands.map((command) => ({
    key: command.key,
    label: commandCaptionOverrides?.[command.key] ?? command.caption,
    icon: getCommandIcon(command),
  }));

  // Find special commands
  const dateFilterCommand = commands?.find((cmd) => getCommandId(cmd) === 'zgridfilter');
  const resetFiltersCommand = commands?.find((cmd) => getCommandId(cmd) === 'resetfilters');
  const refreshCommand = commands?.find((cmd) => getCommandId(cmd) === 'refresh');
  const columnChooserCommand = commands?.find((cmd) => getCommandId(cmd) === 'columnchooser');
  const gridStateCommand = commands?.find((cmd) => getCommandId(cmd) === GRID_STATE_COMMAND_ID);
  const filterPanelCommand = commands?.find((cmd) => getCommandId(cmd) === FILTER_PANEL_COMMAND_ID);
  const showDeletedCommand = commands?.find((cmd) => getCommandId(cmd) === SHOW_DELETED_COMMAND_ID);
  const gridTitleCommand = commands?.find((cmd) => getCommandId(cmd) === 'zgridtitle');

  const renderMobileDateFilter = () => {
    if (!isMobile || !dateFilterCommand || !onDateRangeChange) return null;

    return (
      <Box
        sx={{
          mt: 2,
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <DateRangeFilter
          command={dateFilterCommand}
          onDateRangeChange={onDateRangeChange}
          initialDateRange={initialDateRange}
        />
      </Box>
    );
  };

  if (!visibleCommands || visibleCommands.length === 0) {
    // Show filters, search, and show-deleted even if no other commands
    if (
      (dateFilterCommand && onDateRangeChange) ||
      (showSearchPanel && onSearchChange) ||
      resetFiltersCommand ||
      (filterPanelCommand && filterPanelAvailable && onFilterPanelToggle) ||
      refreshCommand ||
      (showDeletedCommand && onShowDeletedToggle) ||
      columnChooserCommand ||
      (gridStateCommand && (onSaveGridState || onClearGridState))
    ) {
      return (
        <Box ref={toolbarRef} sx={{ mb: 2.5 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            {renderLeftSideButtons()}
            <Stack direction="row" spacing={1} sx={{ ml: 'auto', alignItems: 'center' }}>
              {renderRightSideExtras()}
            </Stack>
          </Box>
          {renderMobileDateFilter()}
        </Box>
      );
    }
    return null;
  }

  return (
    <Box ref={toolbarRef} sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        {renderLeftSideButtons()}

        <Stack
          direction="row"
          spacing={1}
          sx={{
            ml: 'auto',
            alignItems: 'center',
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          <DetailFormActionOverflow
            items={commandActionItems}
            renderItem={(index) => {
              const cmd = overflowCommands[index];
              const icon = getCommandIcon(cmd);
              const commandId = getCommandId(cmd);
              const isShowDeletedCommand = commandId === SHOW_DELETED_COMMAND_ID;
              const isStateCommand = commandId === GRID_STATE_COMMAND_ID;
              const disabled = isShowDeletedCommand ? false : isCommandDisabled(cmd);
              const color = getButtonColor(cmd);
              const caption = commandCaptionOverrides?.[cmd.key] ?? cmd.caption;
              const tooltip = disabled ? cmd.hintForStateDisabled || '' : caption;
              const isCustomCommand =
                cmd.alias === 'custom' ||
                Boolean(cmd.action && !commandRegistry.has(getCommandId(cmd)));
              const showIconOnly = isCustomCommand ? Boolean(normalizeCommandIcon(cmd.icon)) : true;
              return (
                <Tooltip key={cmd.key} title={tooltip} arrow>
                  <span>
                    <Button
                      variant="outlined"
                      color={isShowDeletedCommand && showDeleted ? 'error' : color}
                      {...(isShowDeletedCommand &&
                        showDeleted && { variant: 'contained' as const })}
                      startIcon={showIconOnly || isCustomCommand ? undefined : icon}
                      onClick={(event) => {
                        if (isShowDeletedCommand) {
                          onShowDeletedToggle?.();
                        } else if (isStateCommand) {
                          setGridStateMenuAnchor(event.currentTarget);
                        } else {
                          onCommandClick(cmd);
                        }
                      }}
                      disabled={disabled}
                      size="medium"
                      sx={showIconOnly ? ICON_BUTTON_SX : undefined}
                    >
                      {showIconOnly ? icon : caption}
                    </Button>
                  </span>
                </Tooltip>
              );
            }}
            onSelect={(index, anchorEl) => {
              const cmd = overflowCommands[index];
              const commandId = getCommandId(cmd);
              if (commandId === SHOW_DELETED_COMMAND_ID) {
                onShowDeletedToggle?.();
              } else if (commandId === GRID_STATE_COMMAND_ID) {
                setGridStateMenuAnchor(toolbarRef.current ?? anchorEl);
              } else {
                onCommandClick(cmd);
              }
            }}
            isItemDisabled={(index) => {
              const commandId = getCommandId(overflowCommands[index]);
              return commandId === SHOW_DELETED_COMMAND_ID
                ? false
                : isCommandDisabled(overflowCommands[index]);
            }}
            isItemDestructive={(index) => getButtonColor(overflowCommands[index]) === 'error'}
            moreButtonSx={ICON_BUTTON_SX}
          />
          {gridStateCommand && renderGridStateMenu()}
          {renderRightSideExtras()}
        </Stack>
      </Box>
      {renderMobileDateFilter()}
    </Box>
  );
};
